/**
 * AI Governance API Service - APB-1965
 *
 * HTTP client for /api/v1/ai-governance/* endpoints.
 * Implements all 8 governance endpoints with fetch-based pattern.
 * Follows shadowAgentApi.ts pattern: fetch() + getFullApiUrl() + getAuthHeadersSync()
 */

import { getAuthHeadersSync } from '@shared/utils/authHelpers';
import { getFullApiUrl } from '../../../shared/utils/apiConfig.js';

import type {
  ContainmentRequest,
  ContainmentResponse,
  ForensicTimeline,
  GovernanceAgent,
  GovernanceAgentFilters,
  GovernanceAgentListResponse,
  GovernanceReport,
  GovernanceReportRequest,
  GovernanceShadowAgent,
  GovernanceSummary,
  NotificationStatusUpdate,
  RegulatoryNotification,
  RegulatoryNotificationRequest,
  ShadowAgentListResponse,
} from '../types/index.js';

const BASE_PATH = '/v1/ai-governance';

/** Default request timeout in milliseconds (30 seconds) */
const DEFAULT_TIMEOUT_MS = 30_000;

function getUrl(path: string = ''): string {
  return `${getFullApiUrl()}${BASE_PATH}${path}`;
}

interface ApiFetchOptions extends RequestInit {
  /** Timeout in milliseconds (default: 30000) */
  timeoutMs?: number;
  /** Expected HTTP status codes that should NOT throw (default: 200-299 via response.ok) */
  expectedStatuses?: number[];
}

async function apiFetch<T>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  const { timeoutMs = DEFAULT_TIMEOUT_MS, expectedStatuses, ...fetchOptions } = options;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...getAuthHeadersSync(),
    ...((fetchOptions.headers as Record<string, string>) || {}),
  };

  // AbortController for request timeout + cancellation support
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(getUrl(path), {
      ...fetchOptions,
      headers,
      credentials: 'include',
      signal: controller.signal,
    });

    // Check if status is acceptable
    const isAcceptable = expectedStatuses
      ? expectedStatuses.includes(response.status)
      : response.ok;

    if (!isAcceptable) {
      const errorBody = await response.text().catch(() => '');
      throw new Error(
        `AI Governance API error ${response.status}: ${errorBody}`,
      );
    }

    return response.json();
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error(`AI Governance API request timed out after ${timeoutMs}ms: ${path}`);
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

// ---------------------------------------------------------------------------
// 1. GET /summary — Governance Dashboard Summary
// ---------------------------------------------------------------------------

export async function getGovernanceSummary(): Promise<GovernanceSummary> {
  return apiFetch<GovernanceSummary>('/summary');
}

// ---------------------------------------------------------------------------
// 2. GET /agents — List Agents with Governance Context
// ---------------------------------------------------------------------------

export async function listGovernanceAgents(
  filters?: GovernanceAgentFilters,
): Promise<GovernanceAgentListResponse> {
  const params = new URLSearchParams();

  if (filters?.autonomy_level) {
    params.set('autonomy_level', filters.autonomy_level);
  }
  if (filters?.risk_level) {
    params.set('risk_level', filters.risk_level);
  }
  if (filters?.limit != null) {
    params.set('limit', String(filters.limit));
  }
  if (filters?.offset != null) {
    params.set('offset', String(filters.offset));
  }

  const query = params.toString();
  return apiFetch<GovernanceAgentListResponse>(
    `/agents${query ? `?${query}` : ''}`,
  );
}

// ---------------------------------------------------------------------------
// 3. POST /containment — Request Agent Containment
// ---------------------------------------------------------------------------

export async function requestContainment(
  request: ContainmentRequest,
): Promise<ContainmentResponse> {
  return apiFetch<ContainmentResponse>('/containment', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

// ---------------------------------------------------------------------------
// 4. GET /forensics/{agent_id} — Get Agent Forensic Timeline
// ---------------------------------------------------------------------------

export async function getAgentForensics(
  agentId: string,
  hoursBack?: number,
): Promise<ForensicTimeline> {
  const params = new URLSearchParams();
  if (hoursBack != null) {
    params.set('hours_back', String(hoursBack));
  }

  const query = params.toString();
  return apiFetch<ForensicTimeline>(
    `/forensics/${encodeURIComponent(agentId)}${query ? `?${query}` : ''}`,
  );
}

// ---------------------------------------------------------------------------
// 5. POST /notifications — Create Regulatory Notification
// ---------------------------------------------------------------------------

export async function createRegulatoryNotification(
  request: RegulatoryNotificationRequest,
): Promise<RegulatoryNotification> {
  // POST /notifications returns 201 CREATED — pass as expectedStatuses
  return apiFetch<RegulatoryNotification>('/notifications', {
    method: 'POST',
    body: JSON.stringify(request),
    expectedStatuses: [200, 201],
  });
}

// ---------------------------------------------------------------------------
// 6. PUT /notifications/{id}/status — Update Notification Status
// ---------------------------------------------------------------------------

export async function updateNotificationStatus(
  notificationId: string,
  update: NotificationStatusUpdate,
): Promise<RegulatoryNotification> {
  return apiFetch<RegulatoryNotification>(
    `/notifications/${encodeURIComponent(notificationId)}/status`,
    {
      method: 'PUT',
      body: JSON.stringify(update),
    },
  );
}

// ---------------------------------------------------------------------------
// 7. GET /shadow-agents — Discover Shadow (Unregistered) Agents
// ---------------------------------------------------------------------------

export async function listShadowAgents(): Promise<GovernanceShadowAgent[]> {
  const response = await apiFetch<ShadowAgentListResponse>('/shadow-agents');
  return response.shadow_agents;
}

// ---------------------------------------------------------------------------
// 8. POST /reports — Generate Governance Report
// ---------------------------------------------------------------------------

export async function generateReport(
  request: GovernanceReportRequest,
): Promise<GovernanceReport> {
  return apiFetch<GovernanceReport>('/reports', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}
